import { BuyerLink } from '@choco-admin/types';
import { Divider } from '@heroui/react';
import React, { useMemo } from 'react';

import { AlertCard } from '../../../../../components/AlertCard';
import {
  getBuyerLinkBlock,
  getDistributorLinkBlock,
  getUserAccessWarnings,
} from '../../../../../utils/userEntityAccess';
import { BuyerLinkingField } from '../../BuyerLinking';
import { SupplierLinkingField } from '../../SupplierLinking';
import { UserFormUser } from '../hooks';

interface UserLinkingSectionProps {
  user: UserFormUser;
  userBuyers: BuyerLink[];
  disabled?: boolean;
  /** Live link state, owned by the form so the user type section sees it too. */
  linkedBuyerCount: number;
  linkedDistributorId: string | null;
  onLinkedBuyersChange: (count: number) => void;
  onLinkedDistributorChange: (supplierId: string | null) => void;
}

export const UserLinkingSection: React.FC<UserLinkingSectionProps> = ({
  user,
  userBuyers,
  disabled = false,
  linkedBuyerCount,
  linkedDistributorId,
  onLinkedBuyersChange,
  onLinkedDistributorChange,
}) => {
  // Both fields link and unlink in place, so the rules run on the live state:
  // a buyer linked a moment ago must already block a distributor link.

  const access = {
    isDistributorUser: !!user.supplier,
    linkedDistributorId,
    linkedBuyerCount,
  };
  const warnings = getUserAccessWarnings(access);
  const buyerLinkBlock = getBuyerLinkBlock(access);
  const distributorLinkBlock = getDistributorLinkBlock(access);

  const initialLinkedBuyers = useMemo(
    () =>
      userBuyers.map((buyer) => ({
        value: buyer.id,
        label: `${buyer.name}-${buyer.referenceId || 'N/A'} (${
          buyer.fullAddress || 'No address'
        })`,
        metaData: {
          rolesEnabled: buyer.rolesEnabled,
          buyerUserRole: buyer.role,
        },
      })),
    [userBuyers]
  );

  const initialLinkedSupplier = useMemo(
    () =>
      user.supplierName
        ? {
            value: user.supplierId || '',
            label: `${user.supplierName}-${
              user.supplierReferenceId || 'N/A'
            } (No address)`,
          }
        : null,
    [user.supplierId, user.supplierName, user.supplierReferenceId]
  );

  return (
    <>
      {warnings.map((warning) => (
        <AlertCard
          key={warning.title}
          type="warning"
          title={warning.title}
          message={warning.message}
        />
      ))}

      <BuyerLinkingField
        userId={user.id}
        userName={user.name || ''}
        initialLinkedBuyers={initialLinkedBuyers}
        disabled={disabled}
        linkBlock={buyerLinkBlock}
        showLinkBlockMessage={warnings.length === 0}
        onLinkedBuyersChange={onLinkedBuyersChange}
      />
      <Divider />

      <SupplierLinkingField
        userId={user.id}
        userName={user.name || ''}
        initialLinkedSupplier={initialLinkedSupplier}
        initialRole={user.supplierRole}
        disabled={disabled}
        linkBlock={distributorLinkBlock}
        showLinkBlockMessage={warnings.length === 0}
        onLinkedSupplierChange={onLinkedDistributorChange}
      />
      <Divider />
    </>
  );
};
